import {
	createAtomNameFromOptions,
	createObjectComputedValueAtomName,
	createObjectKeysAtomName,
	createObjectValueAtomName,
	NameOptions,
} from "../extras/reactive-names";
import { afterChange, beforeChange } from "../extras/reactive-plugin";
import {
	getObjectSetChanges,
	getObjectDeleteChanges,
} from "../extras/reactive-plugin-object";
import {
	createAction,
	runAction,
	runTransaction,
} from "../primitives/reactive-action";
import { createAtom } from "../primitives/reactive-atom";
import { createComputedAtom } from "../primitives/reactive-computed";
import { reactiveValueEquals } from "../primitives/reactive-options";
import { Atom, ComputedAtom } from "../primitives/reactive-primitive-types";
import { isTracking, untrack } from "../primitives/reactive-tracking";
import { ContextReactiveOptions } from "./reactive-abstraction-types";
import { getChild, getChildOptions } from "./reactive-children";
import { createProxy, ensureInert } from "./reactive-proxy";
import { getDescriptor } from "./reactive-utils";

type ProxyTarget = object;

enum ValueType {
	atom,
	computed,
}

type ReactiveValue =
	| { type: ValueType.atom; value: Atom }
	| { type: ValueType.computed; value: ComputedAtom<unknown> };

type Reactive = {
	values: {
		[k: string | symbol | number]: ReactiveValue;
	};
	keys: Atom;
};

export const createReactiveObject = <T extends object>(
	inertObject: T,
	reactiveOptions?: ContextReactiveOptions,
	nameOptions?: NameOptions
): T => {
	const atomName = createAtomNameFromOptions(nameOptions);

	const proxy = new Proxy(
		inertObject,
		new ReactiveObjectProxyHandler(
			atomName,
			{
				values: {},
				keys: createAtom(createObjectKeysAtomName(atomName)),
			},
			() => proxy,
			reactiveOptions
		)
	) as T;

	return proxy;
};

class ReactiveObjectProxyHandler implements ProxyHandler<ProxyTarget> {
	#atomName: string;
	#reactive: Reactive;
	#getProxy: () => object;
	#options: { reactiveOptions?: ContextReactiveOptions };

	constructor(
		atomName: string,
		reactive: Reactive,
		getProxy: () => object,
		reactiveOptions?: ContextReactiveOptions
	) {
		this.#atomName = atomName;
		this.#reactive = reactive;
		this.#getProxy = getProxy;
		this.#options = { reactiveOptions };
	}

	get(inertObject: ProxyTarget, p: string | symbol, receiver: unknown) {
		const isReactive = this.#isReactiveProperty(p);
		if (!isReactive) {
			return untrack(() => {
				return Reflect.get(inertObject, p, receiver);
			});
		}

		const { descriptor } = getDescriptor(inertObject, p);
		if (descriptor?.get) {
			const result = this.#readComputedValue(p, descriptor.get);
			return getChild(this.#options, result, () =>
				createProxy(result, getChildOptions(this.#options), {
					parentAtomName: this.#atomName,
					createAtomName: createObjectComputedValueAtomName,
					childIndex: p.toString(),
				})
			);
		}

		this.#readAtomValue(p);

		const result = Reflect.get(inertObject, p, receiver);
		if (typeof result === "function") {
			return createAction(result);
		}

		return getChild(this.#options, result, () =>
			createProxy(result, getChildOptions(this.#options), {
				parentAtomName: this.#atomName,
				createAtomName: createObjectValueAtomName,
				childIndex: p.toString(),
			})
		);
	}

	set(
		inertObject: ProxyTarget,
		p: string | symbol,
		newValue: unknown,
		receiver: unknown
	) {
		const isReactive = this.#isReactiveProperty(p);

		const newStoredValue =
			!isReactive || this.#options.reactiveOptions?.deep === false
				? newValue
				: ensureInert(newValue);

		const { descriptor } = getDescriptor(inertObject, p);
		if (descriptor?.get || descriptor?.set) {
			return runAction(() => {
				return Reflect.set(inertObject, p, newStoredValue, receiver);
			});
		}

		const hasProperty = inertObject.hasOwnProperty(p);
		const currentValue = Reflect.get(inertObject, p, receiver);
		const isChanged = !reactiveValueEquals(currentValue, newStoredValue);

		const changes =
			!this.#hasPlugins() || !isChanged
				? null
				: getObjectSetChanges(
						this.#getProxy(),
						p,
						!hasProperty,
						currentValue,
						newStoredValue
				  );

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		const result = Reflect.set(inertObject, p, newStoredValue, receiver);

		if (result && isReactive) {
			runTransaction(() => {
				if (isChanged) {
					this.#notifyValue(p);
				}
				if (!hasProperty) {
					this.#reactive.keys.reportChanged();
				}
			});
		}

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}

		return result;
	}

	deleteProperty(inertObject: ProxyTarget, p: string | symbol) {
		const isReactive = this.#isReactiveProperty(p);

		const { descriptor } = getDescriptor(inertObject, p);

		const hasProperty = inertObject.hasOwnProperty(p);
		const currentValue = Reflect.get(inertObject, p);
		const isChanged =
			descriptor?.get ||
			descriptor?.set ||
			!reactiveValueEquals(currentValue, undefined);

		const changes =
			!this.#hasPlugins() || !isChanged
				? null
				: getObjectDeleteChanges(this.#getProxy(), p, currentValue);

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		const result = Reflect.deleteProperty(inertObject, p);
		if (result && isReactive) {
			runTransaction(() => {
				if (isChanged) {
					this.#notifyValue(p);
				}
				if (hasProperty) {
					this.#reactive.keys.reportChanged();
				}
			});
		}

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}

		return result;
	}

	ownKeys(inertObject: ProxyTarget) {
		this.#reactive.keys.reportObserved();
		return Reflect.ownKeys(inertObject);
	}

	#isReactiveProperty(p: string | symbol) {
		return (
			(!this.#options.reactiveOptions?.reactiveList ||
				this.#options.reactiveOptions.reactiveList.indexOf(p) >= 0) &&
			(!this.#options.reactiveOptions?.nonReactiveList ||
				this.#options.reactiveOptions.nonReactiveList.indexOf(p) < 0)
		);
	}

	#notifyValue(p: string | symbol | number) {
		const node = this.#reactive.values[p];
		if (node && node.type === ValueType.atom) {
			node.value.reportChanged();
		}
	}

	#readAtomValue(p: string | symbol | number) {
		let node = this.#reactive.values[p];
		if (!node || node.type !== ValueType.atom) {
			if (!isTracking()) {
				return;
			}
			const value = createAtom(
				createObjectValueAtomName(this.#atomName, p.toString()),
				{
					onBecomeUnobservedListener: () => {
						this.#cleanupValue(p);
					},
				}
			);
			node = {
				type: ValueType.atom,
				value,
			};
			this.#reactive.values[p] = node;
		}
		node.value.reportObserved();
	}

	#cleanupValue(p: string | symbol | number) {
		delete this.#reactive.values[p];
	}

	#readComputedValue(p: string | symbol | number, calculate: () => unknown) {
		let node = this.#reactive.values[p];
		if (!node || node.type !== ValueType.computed) {
			const proxy = this.#getProxy();
			if (!isTracking()) {
				return calculate.call(proxy);
			}
			const value = createComputedAtom(
				createObjectComputedValueAtomName(this.#atomName, p.toString()),
				calculate.bind(proxy),
				{
					onBecomeUnobservedListener: () => {
						this.#cleanupValue(p);
					},
				}
			);
			node = {
				type: ValueType.computed,
				value,
			};
			this.#reactive.values[p] = node;
		}
		return node.value.get();
	}

	#hasPlugins() {
		return (this.#options.reactiveOptions?.plugins?.length ?? 0) > 0;
	}
}
